import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Button,
  SearchInput,
  SkeletonText,
  TableChip,
  Pagination,
  CustomSelect,
  SideDrawer,
} from '@/components';
import { parseApiError } from '@/common/utils';
import { V2Table } from '@/common/components';
import { TrashIcon } from '../../components/Icons';
import { uploadQuotationToDrive, formatDriveUploadError } from '../../api';
import { useGoogleWorkspaceConnected } from '../../hooks/useGoogleWorkspaceConnected';
import {
  useCustomerQuotations,
  useQuotations,
  useDeleteCustomerQuotation,
  useQuotationsStateSnapshot,
} from '../hooks';
import {
  CUSTOMER_QUOTE_STATUS_OPTIONS,
  CUSTOMER_QUOTE_STATUS_TONE,
  CUSTOMER_QUOTE_STATUS_LABEL,
  formatQuoteMoney,
  formatQuoteDate,
  encodeQuotationId,
  resolveQuotationMediaUrl,
  showDeliveryLine,
} from '../utils';
import { CustomerQuotationForm } from './CustomerQuotationForm';

const ICON_BTN =
  'w-9 h-9 rounded-lg border bg-white flex items-center justify-center transition-colors focus:outline-none shrink-0 disabled:opacity-40 disabled:cursor-not-allowed';

function RowActions({ cq, companyId, canDelete, onDelete }) {
  const googleDrive = useGoogleWorkspaceConnected(companyId);
  const [isUploading, setIsUploading] = useState(false);
  const { mutate: uploadToDrive } = useMutation({
    mutationFn: () =>
      uploadQuotationToDrive({
        companyId,
        quotationId: cq?.id || cq?.quotation_id,
      }),
    onMutate: () => setIsUploading(true),
    onSuccess: (data) => {
      setIsUploading(false);
      if (data?.webViewLink) {
        toast.success(
          <span>
            Uploaded to Drive!{' '}
            <a
              href={data.webViewLink}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-semibold"
            >
              View in Drive
            </a>
          </span>,
          { duration: 6000 },
        );
      } else {
        toast.success('Quotation uploaded to Google Drive successfully.');
      }
    },
    onError: (err) => {
      setIsUploading(false);
      toast.error(
        formatDriveUploadError(err, 'Failed to upload quotation to Drive.'),
      );
    },
  });

  const openPdf = (e) => {
    e.stopPropagation();
    const url = cq?.pdf_url;
    if (!url) {
      toast.error('PDF is not available yet. Please generate it first.');
      return;
    }
    window.open(resolveQuotationMediaUrl(url), '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="flex items-center justify-end gap-2"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={openPdf}
        disabled={!cq?.pdf_url}
        title={cq?.pdf_url ? 'View PDF' : 'Generate PDF first'}
        className={`${ICON_BTN} border-primary text-primary hover:bg-surface-mint`}
      >
        <svg
          className="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="9" y1="13" x2="15" y2="13" />
          <line x1="9" y1="17" x2="13" y2="17" />
        </svg>
      </button>
      {googleDrive && (
        <button
          type="button"
          disabled={isUploading}
          onClick={() => uploadToDrive()}
          title={
            isUploading
              ? 'Uploading quotation to Google Drive...'
              : 'Upload quotation PDF to Google Drive'
          }
          className={`${ICON_BTN} border-yellow-300 hover:bg-yellow-50`}
        >
          {isUploading ? (
            <span className="w-4 h-4 border-2 border-yellow-400/30 border-t-yellow-500 rounded-full animate-spin" />
          ) : (
            <svg viewBox="0 0 24 24" className="w-4 h-4">
              <path fill="#FBBC05" d="M7.71 3.5L1.15 15l3.43 6 6.57-11.5z" />
              <path fill="#4285F4" d="M22.85 15L16.29 3.5H9.15L15.71 15z" />
              <path fill="#34A853" d="M4.58 21h14.84l3.43-6H1.15z" />
            </svg>
          )}
        </button>
      )}
      {canDelete && (
        <button
          type="button"
          onClick={() => onDelete(cq)}
          title="Delete quotation"
          className={`${ICON_BTN} border-red-200 text-red-500 hover:bg-red-50`}
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export function CustomerQuotationsTab({
  companyId,
  canCreate = false,
  canDelete = false,
}) {
  const navigate = useNavigate();
  const { readSnapshot, saveOrdersState, attachScrollListener, restoreScroll } =
    useQuotationsStateSnapshot();
  const initial = useRef(readSnapshot().orders).current;

  const [search, setSearch] = useState(initial.search || '');
  const [status, setStatus] = useState(initial.status || '');
  const [quotationId, setQuotationId] = useState(initial.quotationId || '');
  const [page, setPage] = useState(initial.page || 1);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const deleteMutation = useDeleteCustomerQuotation();

  useEffect(() => {
    saveOrdersState({ search, status, quotationId, page });
  }, [search, status, quotationId, page, saveOrdersState]);

  useEffect(() => {
    return attachScrollListener('orders');
  }, [attachScrollListener]);

  const params = {
    page,
    ...(search ? { search } : {}),
    ...(status ? { status } : {}),
    ...(quotationId ? { quotation: quotationId } : {}),
  };

  const { data: quotationsData, isLoading: isLoadingQ } = useQuotations(
    companyId,
    { is_active: true },
  );
  const templateOptions = useMemo(() => {
    const quotations = Array.isArray(quotationsData)
      ? quotationsData
      : quotationsData?.results || [];

    return [
      { value: '', label: 'All Templates' },
      ...quotations.map((q) => ({
        value: String(q.id),
        label: q.title + (q.quote_code ? ` (${q.quote_code})` : ''),
      })),
    ];
  }, [quotationsData]);

  const { data, isLoading, isFetching } = useCustomerQuotations(
    companyId,
    params,
    { placeholderData: keepPreviousData },
  );

  const isTableLoading = (isLoading && !data) || isFetching;
  const orders = Array.isArray(data) ? data : data?.results || [];
  const totalPages = data?.total_pages || data?.num_pages || 1;

  const openOrder = (cq) => {
    if (cq.customer_name) {
      try {
        sessionStorage.setItem('quotation-customer-name', cq.customer_name);
        window.dispatchEvent(new Event('quotation-customer-name-updated'));
      } catch {
        // ignore
      }
    }
    navigate(`/quotations/customer/${encodeQuotationId(cq.id)}`, {
      state: { customerName: cq.customer_name },
    });
  };

  const columns = [
    {
      id: 'number',
      label: 'Quote #',
      defaultWidth: 170,
      minWidth: 140,
      resizable: true,
      skeleton: <SkeletonText width="w-24" height="h-4" />,
      cell: (cq) => (
        <span className="font-mono text-xs font-semibold text-primary truncate block">
          {cq.quote_number}
        </span>
      ),
    },
    {
      id: 'customer',
      label: 'Customer',
      defaultWidth: 180,
      minWidth: 130,
      resizable: true,
      skeleton: <SkeletonText width="w-28" height="h-4" />,
      cell: (cq) => (
        <span className="font-medium truncate block">
          {cq.customer_name || '—'}
        </span>
      ),
    },
    {
      id: 'phone',
      label: 'Phone',
      defaultWidth: 140,
      minWidth: 110,
      resizable: true,
      skeleton: <SkeletonText width="w-24" height="h-4" />,
      cell: (cq) => (
        <span className="text-xs text-text-muted">
          {cq.customer_phone || '—'}
        </span>
      ),
    },
    {
      id: 'template',
      label: 'Template',
      defaultWidth: 180,
      minWidth: 120,
      resizable: true,
      skeleton: <SkeletonText width="w-24" height="h-4" />,
      cell: (cq) => (
        <span className="text-xs text-text-secondary truncate block">
          {cq.quotation_title || '—'}
        </span>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      defaultWidth: 188,
      minWidth: 120,
      resizable: true,
      skeleton: <SkeletonText width="w-20" height="h-5" />,
      cell: (cq) => {
        const label =
          CUSTOMER_QUOTE_STATUS_LABEL[cq.status] || cq.status || 'UNKNOWN';
        return (
          <div className="block w-full min-w-0 max-w-full overflow-hidden">
            <TableChip
              tone={CUSTOMER_QUOTE_STATUS_TONE[cq.status] || 'neutral'}
              title={label}
              className="!flex w-full max-w-full min-w-0 justify-start overflow-hidden"
            >
              <span className="block min-w-0 truncate">{label}</span>
            </TableChip>
          </div>
        );
      },
    },
    {
      id: 'total',
      label: 'Total',
      defaultWidth: 130,
      minWidth: 100,
      resizable: true,
      skeleton: <SkeletonText width="w-16" height="h-4" />,
      cell: (cq) => {
        const total = formatQuoteMoney(cq.total_amount, cq.currency);
        return (
          <span className="block w-full min-w-0 max-w-full overflow-hidden">
            <span className="font-semibold block truncate" title={total}>
              {total}
            </span>
            {showDeliveryLine(cq.delivery_term, cq.delivery_charge) && (
              <span className="text-[10px] text-text-muted block truncate">
                Includes {cq.delivery_term || 'Delivery'}:{' '}
                {formatQuoteMoney(cq.delivery_charge, cq.currency)}
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: 'date',
      label: 'Date',
      defaultWidth: 120,
      minWidth: 100,
      resizable: true,
      skeleton: <SkeletonText width="w-20" height="h-4" />,
      cell: (cq) => (
        <span className="text-xs text-text-muted">
          {formatQuoteDate(cq.created_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      defaultWidth: 140,
      minWidth: 120,
      resizable: false,
      align: 'center',
      stopPropagation: true,
      skeleton: <SkeletonText width="w-16" height="h-5" className="mx-auto" />,
      cell: (cq) => (
        <RowActions
          cq={cq}
          companyId={companyId}
          canDelete={canDelete}
          onDelete={setPendingDelete}
        />
      ),
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      restoreScroll('orders');
    }
  }, [isLoading, data, restoreScroll]);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(
      { companyId, customerQuotationId: pendingDelete.id },
      {
        onSuccess: () => {
          toast.success('Customer quotation deleted.');
          setPendingDelete(null);
        },
        onError: (err) => {
          toast.error(
            parseApiError(err).message || 'Failed to delete quotation.',
          );
        },
      },
    );
  };

  return (
    <>
      {pendingDelete && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-overlay-backdrop backdrop-blur-sm"
            onClick={() => !deleteMutation.isPending && setPendingDelete(null)}
            aria-hidden="true"
          />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full space-y-4 border border-border-default">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                <TrashIcon className="w-4 h-4" />
              </span>
              <p className="text-h6 font-bold text-text-primary">
                Delete this quotation?
              </p>
            </div>
            <p className="text-body-sm text-text-secondary leading-relaxed">
              This action cannot be undone. The customer quotation and its
              generated PDF will be permanently removed.
            </p>
            <div className="flex gap-2 justify-end pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setPendingDelete(null)}
                disabled={deleteMutation.isPending}
                className="h-9 px-4 text-xs font-medium"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
                className="h-9 px-4 text-xs font-medium !bg-red-500 hover:!bg-red-600 text-white border-transparent"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete Quotation'}
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden w-full">
        <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center justify-between gap-3 mb-4 w-full shrink-0">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center flex-wrap gap-2.5 sm:gap-3 flex-1 min-w-0">
            <SearchInput
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by customer name, phone…"
              id="customer-quotation-search"
              className="w-full sm:flex-1 sm:min-w-[200px] lg:max-w-[320px]"
              inputClassName="!h-10"
            />
            <CustomSelect
              value={quotationId}
              onChange={(val) => {
                setQuotationId(val);
                setPage(1);
              }}
              options={templateOptions}
              isLoading={isLoadingQ}
              searchable
              searchPlaceholder="Search templates…"
              hideCheckIcon
              textSizeClass="text-body-sm"
              className="w-full sm:w-[180px] md:w-[200px] h-10 shrink-0"
            />
            <CustomSelect
              value={status}
              onChange={(val) => {
                setStatus(val);
                setPage(1);
              }}
              options={CUSTOMER_QUOTE_STATUS_OPTIONS}
              hideCheckIcon
              textSizeClass="text-body-sm"
              className="w-full sm:w-[140px] md:w-[150px] h-10 shrink-0"
            />
            {(search || status || quotationId) && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setStatus('');
                  setQuotationId('');
                  setPage(1);
                }}
                className="text-xs font-medium text-text-secondary hover:text-primary transition-colors whitespace-nowrap px-1 py-1 shrink-0 self-start sm:self-auto"
              >
                Clear filters
              </button>
            )}
          </div>
          {canCreate && (
            <Button
              id="new-customer-quotation-btn"
              type="button"
              onClick={() => setIsCreateOpen(true)}
              className="h-10 px-4 text-sm shrink-0 flex items-center gap-1.5 self-end sm:self-auto"
            >
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              New
            </Button>
          )}
        </div>

        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <V2Table
            tight
            columns={columns}
            data={orders}
            rowKey="id"
            storageKey="customer_quotations_table_column_widths"
            loading={isTableLoading}
            loadingRowCount={8}
            onRowClick={openOrder}
            empty="No customer quotations found."
          />
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex justify-end shrink-0">
            <Pagination
              page={page}
              totalPages={totalPages}
              onChange={setPage}
            />
          </div>
        )}

        <SideDrawer
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          className="q-drawer"
          title="New Customer Quotation"
          width="xl"
        >
          <CustomerQuotationForm
            companyId={companyId}
            onCancel={() => setIsCreateOpen(false)}
            onSuccess={(data) => {
              setIsCreateOpen(false);
              if (data?.id) {
                if (data.customer_name) {
                  try {
                    sessionStorage.setItem(
                      'quotation-customer-name',
                      data.customer_name,
                    );
                    window.dispatchEvent(
                      new Event('quotation-customer-name-updated'),
                    );
                  } catch {
                    // ignore
                  }
                }
                navigate(`/quotations/customer/${encodeQuotationId(data.id)}`, {
                  state: { customerName: data.customer_name },
                });
              }
            }}
          />
        </SideDrawer>
      </div>
    </>
  );
}
